import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Edit as FormIcon,
  ViewList as TableViewIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../auth/useAuth';
import { useHedgeFundData } from '../../contexts/HedgeFundDataContext';
import DataEntryForm from './DataEntryForm';
import BrowseEditView from './BrowseEditView';

interface MonitorField {
  FieldID: number;
  FieldName: string;
  RiskIndicatorID: number;
  AggregationFieldID?: number;
}

const DataManagement: React.FC = () => {
  const { getAccessToken } = useAuth();
  const { funds } = useHedgeFundData();
  
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // View mode state
  const [viewMode, setViewMode] = useState<'form' | 'table'>('table');
  
  // Monitor fields for import
  const [monitorFields, setMonitorFields] = useState<MonitorField[]>([]);
  
  // Cached monitor data for Browse & Edit view
  const [cachedMonitorData, setCachedMonitorData] = useState<any[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importStep, setImportStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState<{
    success: number;
    errors: number;
    warnings: number;
    details: string[];
  } | null>(null);
  
  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load monitor fields on component mount
  useEffect(() => {
    loadMonitorFields();
  }, []);

  const loadMonitorFields = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-fields`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMonitorFields(data);
      }
    } catch (err) {
      console.error('Failed to load monitor fields:', err);
    }
  };

  // Import functionality
  const handleImportProcess = async () => {
    if (!selectedFile || !fieldMapping.defaultFund || !fieldMapping.defaultField) {
      setError('Please select a file and configure the import settings');
      return;
    }

    try {
      setImportStep(2);
      setImportProgress(0);
      setError(null);

      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('backstop_id', fieldMapping.defaultFund);
      formData.append('field_id', fieldMapping.defaultField);

      const progressInterval = setInterval(() => {
        setImportProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 300);

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/import/monitor-data`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData
        }
      );

      clearInterval(progressInterval);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Import failed');
      }

      const result = await response.json();
      setImportProgress(100);

      setImportResults({
        success: result.imported_count,
        errors: result.errors ? result.errors.length : 0,
        warnings: 0,
        details: result.errors || []
      });

      if (result.imported_count > 0) {
        setSuccess(result.message);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setImportStep(1);
      setImportProgress(0);
    }
  };

  const handleImportClose = () => {
    setImportDialogOpen(false);
    setImportStep(0);
    setSelectedFile(null);
    setFieldMapping({});
    setImportProgress(0);
    setImportResults(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
          Data Management
        </Typography>
        
        <Tabs 
          value={viewMode} 
          onChange={(e, newValue) => setViewMode(newValue)}
          sx={{ minHeight: 40 }}
        >
          <Tab 
            icon={<FormIcon />} 
            iconPosition="start" 
            label="Update Data" 
            value="form"
            sx={{ minHeight: 40 }}
          />
          <Tab 
            icon={<TableViewIcon />} 
            iconPosition="start" 
            label="Browse Data" 
            value="table"
            sx={{ minHeight: 40 }}
          />
        </Tabs>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Conditional view rendering */}
      {viewMode === 'form' ? (
        <DataEntryForm />
      ) : (
        <BrowseEditView />
      )}

      {/* Import Dialog */}
      <Dialog 
        open={importDialogOpen} 
        onClose={handleImportClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Monitor Data from Excel</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Stepper activeStep={importStep} orientation="vertical">
              <Step>
                <StepLabel>Select Excel File</StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Choose an Excel file containing monitor data. The file should have columns for Fund, Field, Date, and Value.
                  </Typography>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setSelectedFile(file);
                        setImportStep(1);
                      }
                    }}
                  />
                  
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Choose File
                    </Button>
                    {selectedFile && (
                      <Typography variant="body2" color="text.secondary">
                        Selected: {selectedFile.name}
                      </Typography>
                    )}
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>Configure Import</StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Configure the import settings for your monitor data.
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Default Fund</InputLabel>
                        <Select
                          value={fieldMapping.defaultFund || ''}
                          onChange={(e) => setFieldMapping(prev => ({ ...prev, defaultFund: e.target.value }))}
                          label="Default Fund"
                        >
                          <MenuItem value="">Select Fund</MenuItem>
                          {funds.map(fund => (
                            <MenuItem key={fund.BackstopID} value={fund.BackstopID.toString()}>
                              {fund.FundName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Default Field</InputLabel>
                        <Select
                          value={fieldMapping.defaultField || ''}
                          onChange={(e) => setFieldMapping(prev => ({ ...prev, defaultField: e.target.value }))}
                          label="Default Field"
                        >
                          <MenuItem value="">Select Field</MenuItem>
                          {monitorFields.map(field => (
                            <MenuItem key={field.FieldID} value={field.FieldID.toString()}>
                              {field.FieldName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleImportProcess}
                      disabled={!fieldMapping.defaultFund || !fieldMapping.defaultField}
                    >
                      Start Import
                    </Button>
                    <Button onClick={() => setImportStep(0)}>
                      Back
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>Import Progress</StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Importing your monitor data...
                  </Typography>
                  
                  <LinearProgress 
                    variant="determinate" 
                    value={importProgress} 
                    sx={{ mb: 2 }}
                  />
                  
                  <Typography variant="body2" color="text.secondary">
                    {importProgress}% complete
                  </Typography>
                  
                  {importResults && (
                    <Card sx={{ mt: 2 }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          Import Results
                        </Typography>
                        
                        <List dense>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={`${importResults.success} records imported successfully`}
                            />
                          </ListItem>
                          
                          {importResults.errors > 0 && (
                            <ListItem>
                              <ListItemIcon>
                                <ErrorIcon color="error" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={`${importResults.errors} errors encountered`}
                              />
                            </ListItem>
                          )}
                          
                          {importResults.warnings > 0 && (
                            <ListItem>
                              <ListItemIcon>
                                <WarningIcon color="warning" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={`${importResults.warnings} warnings`}
                              />
                            </ListItem>
                          )}
                        </List>
                        
                        {importResults.details.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              Details:
                            </Typography>
                            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                              {importResults.details.map((detail, index) => (
                                <Typography key={index} variant="body2" color="text.secondary">
                                  {detail}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </StepContent>
              </Step>
            </Stepper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportClose}>
            {importResults ? 'Close' : 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataManagement;
